import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Card,
    Col,
    Form,
    Row,
    Table,
    Button,
    Badge,
    Spinner as BootstrapSpinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Header from "../../../components/Layout/Header";
import Sidebar from "../../../components/Layout/Sidebar";
import Main from "../../../components/Layout/Main";
import Pagination from "../../../components/Layout/Pagination";
import PageSpinner from "../../../components/Layout/spinner";
import SweetAlertWrapper from "../../../components/Layout/sweetAlert";

import CustomersContext from "../../../context/otogas/customers/customersContext";
import AuthContext from "../../../context/auth/authContext";

import {
    download_partnership_sales,
    list_partner_stations,
} from "../../../api/otogas";

import { formatDate as formatteDate } from "../../../api/util";

const ListPartnershipSales = () => {
    const customersContext = useContext(CustomersContext);
    const {
        partnership_sales = [],
        partnership_salesTotalRecords = 0,
        listPartnershipSales,
        notification,
        clear_notification,
        totalLitres = 0,
        totalAmounts = 0,
    } = customersContext;

    const authContext = useContext(AuthContext);
    const { user } = authContext;

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(15);

    const [filters, setFilters] = useState({
        stationCode: "",
        shiftNumber: "",
        startDate: "",
        endDate: "",
        orderByColumn: "DateCreated",
        isDescending: true,
    });

    const [loading, setLoading] = useState(false);
    const [download_loading, setDownloadLoading] = useState(false);

    const [partnerStations, setPartnerStations] = useState([]);
    const [stationsLoading, setStationsLoading] = useState(false);

    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState({ text: "", type: "" });

    const [authReady, setAuthReady] = useState(false);

    useEffect(() => {
        if (user && typeof user === "object") {
            setAuthReady(true);
        }
    }, [user]);

    useEffect(() => {
        if (notification) {
            setAlertMessage({
                text: notification.text || "Done",
                type: notification.type || "info",
            });
            setShowAlert(true);
        }
    }, [notification]);

    const userPartnerCode = useMemo(() => {
        if (!user) return "";
        return String(user.PartnerCode ?? user.partnerCode ?? user.partner_code ?? "").trim();
    }, [user]);

    const getStationCode = (station) =>
        String(station?.stationCode ?? station?.StationCode ?? "").trim();

    const getStationName = (station) =>
        String(station?.stationName ?? station?.StationName ?? getStationCode(station)).trim();

    const selectedStation = useMemo(() => {
        return (partnerStations || []).find(
            (station) => getStationCode(station) === filters.stationCode
        );
    }, [partnerStations, filters.stationCode]);

    useEffect(() => {
        const fetchPartnerStations = async () => {
            if (!authReady || !userPartnerCode) return;

            try {
                setStationsLoading(true);

                const res = await list_partner_stations(userPartnerCode);

                if (res?.responseCode === 1) {
                    setPartnerStations(res.responseObject || []);
                } else {
                    setPartnerStations([]);
                }
            } catch (error) {
                console.error("Error fetching partner stations:", error);
                setPartnerStations([]);
            } finally {
                setStationsLoading(false);
            }
        };

        fetchPartnerStations();
    }, [authReady, userPartnerCode]);

    const hasActiveFilters = useMemo(() => {
        const hasStation = !!String(filters.stationCode || "").trim();
        const hasShift = !!String(filters.shiftNumber || "").trim();
        const hasDates = !!(filters.startDate || filters.endDate);
        return hasStation || hasShift || hasDates;
    }, [filters.stationCode, filters.shiftNumber, filters.startDate, filters.endDate]);

    const downloadLabel = hasActiveFilters ? "Selected Range Sales" : "Month to Date Sales";

    const parseDate = (dateStr) => (dateStr ? new Date(dateStr) : null);

    const formatLocalYYYYMMDD = (date) => {
        if (!date) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const handleDateRangeChange = (dates) => {
        const [start, end] = dates;

        setCurrentPage(1);
        setFilters((prev) => ({
            ...prev,
            startDate: formatLocalYYYYMMDD(start),
            endDate: formatLocalYYYYMMDD(end),
        }));
    };

    const toPascalCase = (str) =>
        (str || "")
            .replace(/(^\w|[A-Z]|\b\w)/g, (m) => m.toUpperCase())
            .replace(/[\s_]+/g, "");

    const handleSort = (column) => {
        const formattedColumn = toPascalCase(column);

        setCurrentPage(1);
        setFilters((prev) => ({
            ...prev,
            orderByColumn: formattedColumn,
            isDescending:
                prev.orderByColumn === formattedColumn ? !prev.isDescending : true,
        }));
    };

    const fetchSales = async (page, size, f) => {
        if (!listPartnershipSales || !authReady || !userPartnerCode) return;

        try {
            setLoading(true);

            await listPartnershipSales(
                userPartnerCode,
                String(f.stationCode || "").trim(),
                String(f.shiftNumber || "").trim(),
                f.startDate || "",
                f.endDate || "",
                page,
                size,
                f.orderByColumn || "DateCreated",
                !!f.isDescending
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authReady) return;
        if (!userPartnerCode) return;

        fetchSales(currentPage, itemsPerPage, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        authReady,
        userPartnerCode,
        currentPage,
        itemsPerPage,
        filters.stationCode,
        filters.shiftNumber,
        filters.startDate,
        filters.endDate,
        filters.orderByColumn,
        filters.isDescending,
    ]);

    const handleApplyFilters = () => {
        setCurrentPage(1);
        fetchSales(1, itemsPerPage, filters);
    };

    const handleReset = () => {
        setCurrentPage(1);
        setFilters({
            stationCode: "",
            shiftNumber: "",
            startDate: "",
            endDate: "",
            orderByColumn: "DateCreated",
            isDescending: true,
        });
    };

    const handle_downloads = async (func, setLoading) => {
        setLoading(true);

        try {
            const res = await func();

            if (res === "File not found") {
                setAlertMessage({
                    text: "No transactions found",
                    type: "info",
                });
                setShowAlert(true);
            } else if (res === "You are not authorized to download this file") {
                setAlertMessage({
                    text: "You are not authorized to download this file",
                    type: "info",
                });
                setShowAlert(true);
            } else {
                return res;
            }
        } catch (error) {
            console.error("Error downloading the file:", error);
            setAlertMessage({
                text: "An error occurred while downloading the file",
                type: "danger",
            });
            setShowAlert(true);
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil((partnership_salesTotalRecords || 0) / itemsPerPage);

    const totalsLabel = useMemo(() => {
        const hasStation = !!String(filters.stationCode || "").trim();
        const hasShift = !!String(filters.shiftNumber || "").trim();
        const hasDates = !!(filters.startDate || filters.endDate);

        return !hasStation && !hasShift && !hasDates ? "Month to date" : "Range";
    }, [filters.stationCode, filters.shiftNumber, filters.startDate, filters.endDate]);

    const SortHint = ({ col, label }) => (
        <span
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => handleSort(col)}
            title="Click to sort"
        >
            {label}{" "}
            {filters.orderByColumn === toPascalCase(col) && (
                <span className="text-muted">
                    {filters.isDescending ? "▼" : "▲"}
                </span>
            )}
        </span>
    );

    if (!authReady) {
        return (
            <>
                <Header />
                <Sidebar />
                <Main>
                    <div className="py-5">
                        <PageSpinner />
                    </div>
                </Main>
            </>
        );
    }

    return (
        <>
            <Header />
            <Sidebar />
            <Main>
                <SweetAlertWrapper
                    show={showAlert}
                    title="Notification"
                    message={<div>{alertMessage.text}</div>}
                    onConfirm={() => {
                        setShowAlert(false);
                        clear_notification?.();
                    }}
                    confirmBtnText="OK"
                    type={alertMessage.type || "info"}
                />

                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <h2 className="mb-1">Partner Sales</h2>
                    </div>

                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-secondary"
                            onClick={handleReset}
                            disabled={!userPartnerCode}
                        >
                            Reset
                        </Button>

                        <Button
                            variant="primary"
                            onClick={handleApplyFilters}
                            disabled={loading || !userPartnerCode}
                        >
                            {loading ? "Loading…" : "Apply"}
                        </Button>
                    </div>
                </div>

                {!userPartnerCode && (
                    <Card className="border-0 shadow-sm mb-3">
                        <Card.Body>
                            <h5 className="mb-1">Partner not assigned</h5>
                            <div className="text-muted" style={{ fontSize: 13 }}>
                                Your account has no PartnerCode set. Please ask an admin to assign a PartnerCode to your user.
                            </div>

                            <div className="mt-2" style={{ fontFamily: "monospace", fontSize: 12 }}>
                                UserCode: {user?.userCode || "—"} | Email: {user?.email || "—"}
                            </div>
                        </Card.Body>
                    </Card>
                )}

                <Card className="shadow-sm border-0 mb-3">
                    <Card.Body>
                        <Row className="g-3 align-items-center">
                            <Col md={3}>
                                <Form.Select
                                    value={filters.stationCode}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setFilters((prev) => ({
                                            ...prev,
                                            stationCode: e.target.value,
                                        }));
                                    }}
                                    disabled={!userPartnerCode || stationsLoading}
                                >
                                    <option value="">
                                        {stationsLoading ? "Loading stations..." : "All Stations"}
                                    </option>

                                    {(partnerStations || []).map((station) => {
                                        const stationCode = getStationCode(station);
                                        const stationName = getStationName(station);

                                        return (
                                            <option key={stationCode} value={stationCode}>
                                                {stationName} ({stationCode})
                                            </option>
                                        );
                                    })}
                                </Form.Select>
                            </Col>

                            <Col md={2}>
                                <Form.Control
                                    type="text"
                                    placeholder="Shift (optional)"
                                    value={filters.shiftNumber}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setFilters((prev) => ({
                                            ...prev,
                                            shiftNumber: e.target.value,
                                        }));
                                    }}
                                    disabled={!userPartnerCode}
                                />
                            </Col>

                            <Col md={3}>
                                <DatePicker
                                    selected={parseDate(filters.startDate)}
                                    onChange={handleDateRangeChange}
                                    startDate={parseDate(filters.startDate)}
                                    endDate={parseDate(filters.endDate)}
                                    selectsRange
                                    className="form-control"
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="Select Date Range"
                                    isClearable
                                    popperPlacement="bottom"
                                    shouldCloseOnSelect={false}
                                    disabled={!userPartnerCode}
                                />
                            </Col>

                            <Col md={2}>
                                <div className="d-flex flex-wrap gap-2">
                                    {filters.stationCode && (
                                        <Badge bg="secondary" className="px-3 py-2">
                                            Station: {selectedStation ? getStationName(selectedStation) : filters.stationCode}
                                        </Badge>
                                    )}

                                    {filters.shiftNumber && (
                                        <Badge bg="secondary" className="px-3 py-2">
                                            Shift: {filters.shiftNumber}
                                        </Badge>
                                    )}

                                    {(filters.startDate || filters.endDate) && (
                                        <Badge bg="secondary" className="px-3 py-2">
                                            Dates: {filters.startDate || "…"} → {filters.endDate || "…"}
                                        </Badge>
                                    )}
                                </div>
                            </Col>

                            <Col md={2} className="d-flex justify-content-md-end">
                                <Button
                                    className="btn btn-success"
                                    onClick={() =>
                                        handle_downloads(
                                            () =>
                                                download_partnership_sales(
                                                    userPartnerCode,
                                                    String(filters.stationCode || "").trim(),
                                                    String(filters.shiftNumber || "").trim(),
                                                    filters.startDate || "",
                                                    filters.endDate || "",
                                                    filters.orderByColumn || "DateCreated",
                                                    !!filters.isDescending
                                                ),
                                            setDownloadLoading
                                        )
                                    }
                                    disabled={download_loading || !userPartnerCode}
                                >
                                    {download_loading ? (
                                        <>
                                            <BootstrapSpinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />{" "}
                                            Downloading...
                                        </>
                                    ) : (
                                        `Download ${downloadLabel}`
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Row className="g-3 mb-3">
                    <Col md={4}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <div className="text-muted" style={{ fontSize: 13 }}>
                                    Fuelling Events
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 700 }}>
                                    {partnership_salesTotalRecords || 0}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <div className="text-muted" style={{ fontSize: 13 }}>
                                    ({totalsLabel}) Litres
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 700 }}>
                                    {Number(totalLitres || 0).toFixed(2)}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <div className="text-muted" style={{ fontSize: 13 }}>
                                    ({totalsLabel}) Amount
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 700 }}>
                                    {Number(totalAmounts || 0).toFixed(2)}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Card className="shadow-sm border-0">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div style={{ fontWeight: 700 }}>Sales</div>

                            <div className="d-flex align-items-center gap-2">
                                <Form.Select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setItemsPerPage(Number(e.target.value));
                                    }}
                                    style={{ width: 120 }}
                                    disabled={!userPartnerCode}
                                >
                                    {[10, 15, 20, 50].map((n) => (
                                        <option key={n} value={n}>
                                            {n}/page
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                        </div>

                        {loading ? (
                            <PageSpinner />
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead style={{ position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                                        <tr>
                                            <th style={{ width: 60 }}>#</th>
                                            <th>Sale Id</th>
                                            <th>
                                                <SortHint col="ShiftNumber" label="Shift" />
                                            </th>
                                            <th>Station</th>
                                            <th>
                                                <SortHint col="Quantity" label="Quantity (Litres)" />
                                            </th>
                                            <th>Vehicle</th>
                                            <th>Dispenser</th>
                                            <th>Nozzle</th>
                                            <th>
                                                <SortHint col="Price" label="Price/Litre(KSH)" />
                                            </th>
                                            <th>
                                                <SortHint col="Amount" label="Amount (KSH)" />
                                            </th>
                                            <th>Payment Type</th>
                                            <th>
                                                <SortHint col="DateCreated" label="Date" />
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {!userPartnerCode ? (
                                            <tr>
                                                <td colSpan="12" className="text-center text-muted py-5">
                                                    PartnerCode is required. Please contact admin to assign your account a PartnerCode.
                                                </td>
                                            </tr>
                                        ) : (partnership_sales || []).length === 0 ? (
                                            <tr>
                                                <td colSpan="12" className="text-center text-muted py-5">
                                                    No sales found for the selected filters.
                                                </td>
                                            </tr>
                                        ) : (
                                            partnership_sales.map((sale, index) => (
                                                <tr key={`${sale.saleId || "x"}-${index}`}>
                                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>

                                                    <td style={{ fontFamily: "monospace", fontSize: 12 }}>
                                                        {sale.saleId || "-"}
                                                    </td>

                                                    <td>{sale.shiftNumber || "-"}</td>

                                                    <td>
                                                        <div style={{ fontWeight: 600 }}>
                                                            {sale.stationName || "-"}
                                                        </div>
                                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                                            {sale.stationCode || ""}
                                                        </div>
                                                    </td>

                                                    <td style={{ fontWeight: 600 }}>
                                                        {Number(sale.quantity || 0).toFixed(2)}
                                                    </td>

                                                    <td>{sale.vehicleRegistrationNumber || "-"}</td>
                                                    <td>{sale.dispenserName || "-"}</td>
                                                    <td>{sale.nozzleName || "-"}</td>

                                                    <td style={{ fontWeight: 700 }}>
                                                        {Number(sale.price || 0).toFixed(2)}
                                                    </td>

                                                    <td style={{ fontWeight: 700 }}>
                                                        {Number(sale.amount || 0).toFixed(2)}
                                                    </td>

                                                    <td>
                                                        <Badge bg="light" text="dark" className="border">
                                                            {sale.paymentTypeName || "Unknown"}
                                                        </Badge>
                                                    </td>

                                                    <td>
                                                        {sale.dateCreated ? formatteDate(sale.dateCreated) : "-"}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                <div className="mt-3">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        setCurrentPage={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={partnership_salesTotalRecords}
                    />
                </div>
            </Main>
        </>
    );
};

export default ListPartnershipSales;